import { config } from "./config/GameConfig";
import { bezierEaseInOut, clamp } from "./core/math";
import { Time } from "./core/Time";
import { WorldBounds } from "./core/WorldBounds";
import { BubbleManager } from "./entities/BubbleManager";
import { Divider } from "./entities/Divider";
import { PointerInput } from "./input/PointerInput";
import { ForceField } from "./physics/ForceField";
import { MergeSystem } from "./physics/MergeSystem";
import { PhysicsWorld } from "./physics/PhysicsWorld";
import { GameRenderer } from "./render/GameRenderer";
import { AudioEngine } from "./audio/AudioEngine";
import type { ScoreHud } from "./ui/ScoreHud";
import { WakeOverlay } from "./ui/WakeOverlay";
import { StartOverlay } from "./ui/StartOverlay";
import type { Level, LevelObject } from "./levels/types";

export type GameHooks = {
  onWinContinue: () => void;
  onOpenPanel?: () => void;
};

type LineLive = {
  thickness: number;
  wiggleAmp: number;
  sizePulseAmp: number;
  wiggleSpeed: number;
  sizePulseSpeed: number;
};

export class Game {
  readonly bounds = new WorldBounds();
  readonly time = new Time();
  readonly renderer: GameRenderer;

  private physics!: PhysicsWorld;
  private field!: ForceField;
  private divider!: Divider;
  private bubbles!: BubbleManager;
  private merge!: MergeSystem;
  private input?: PointerInput;
  private hud: ScoreHud | null = null;
  private tweak: { dispose: () => void } | null = null;
  private readonly unbind: Array<() => void> = [];
  private audio!: AudioEngine;
  private wake!: WakeOverlay;
  private intro!: StartOverlay;
  private score = 0;
  private hoverOnLine = false;
  private hoverAbove: boolean | null = null;
  private lastPluckX = Number.NaN;
  private hoverBubbleId: number | null = null;
  private hoverX = 0;
  private hoverY = 0;
  private hasHover = false;
  private phase: "title" | "play" | "win" | "won" | "lose" | "lost" = "title";
  private winT = 0;
  private lineBaseline!: LineLive;

  private playing(): boolean {
    return this.phase === "play";
  }

  private toggleHud(): void {
    document.documentElement.classList.toggle("onirya-hud");
  }

  constructor(
    private readonly host: HTMLElement,
    readonly level: Level,
    private readonly hooks: GameHooks = { onWinContinue: () => this.returnToTitle() },
  ) {
    this.renderer = new GameRenderer(config, this.bounds);
  }

  async start(): Promise<void> {
    await this.renderer.init(this.host);

    this.physics = new PhysicsWorld(config, this.bounds);
    this.field = new ForceField(config, this.bounds);
    this.divider = new Divider(config, this.bounds);
    this.bubbles = new BubbleManager(config, this.physics, this.bounds);
    this.merge = new MergeSystem(this.bubbles);
    this.audio = new AudioEngine(config);
    this.wake = new WakeOverlay(this.host, () => this.afterWake(), this.level.copy);
    this.intro = new StartOverlay(
      this.host,
      () => this.beginPlay(),
      this.level.word,
      this.level.copy.start,
      this.hooks.onOpenPanel,
    );
    this.spawnLevelObjects();
    this.lineBaseline = this.snapshotLine();
    const unlock = () => {
      if (this.phase === "title") return;
      this.audio.unlock();
    };
    window.addEventListener("pointerdown", unlock, { capture: true });
    window.addEventListener("keydown", unlock);
    this.unbind.push(() => {
      window.removeEventListener("pointerdown", unlock, { capture: true });
      window.removeEventListener("keydown", unlock);
    });
    if (import.meta.env.DEV) {
      const onHud = (e: KeyboardEvent) => {
        if (e.ctrlKey && !e.metaKey && !e.altKey && e.code === "KeyH") {
          e.preventDefault();
          if (!e.repeat) this.toggleHud();
        }
      };
      window.addEventListener("keydown", onHud);
      this.unbind.push(() => window.removeEventListener("keydown", onHud));
    }
    this.bubbles.onMeeting = (points) => {
      if (!this.playing()) return;
      this.score = Math.min(config.score.winAt, this.score + points);
      this.audio.meet(points);
    };
    this.bubbles.onAbsorb = (radius, above) => {
      this.audio.absorb(radius / Math.max(1, config.bubble.maxRadius), above);
    };
    this.bubbles.onMerge = (radius, above, otherRadius) => {
      const maxR = Math.max(1, this.bubbles.maxAbsorbRadius());
      this.audio.merge(radius / maxR, above, otherRadius / maxR);
    };
    this.bubbles.onSpawn = (above, x) => {
      this.audio.bell(above, x / Math.max(1, this.bounds.width));
    };

    this.physics.onCollision((a, b) => this.merge.handle(a, b));
    this.bindInput();
    if (import.meta.env.DEV) {
      const [{ TweakPanel }, { ScoreHud }] = await Promise.all([
        import("./debug/TweakPanel"),
        import("./ui/ScoreHud"),
      ]);
      this.hud = new ScoreHud(this.host);
      this.tweak = new TweakPanel(config, this.level);
      (window as unknown as { onirya: Game }).onirya = this;
      this.syncHud();
    }

    this.renderer.app.ticker.add((ticker) => this.tick(ticker.deltaMS));
  }

  showTitle(): void {
    this.intro.reveal();
    this.phase = "title";
  }

  dispose(): void {
    for (const fn of this.unbind) fn();
    this.unbind.length = 0;
    this.input?.destroy();
    this.tweak?.dispose();
    this.hud?.remove();
    this.intro.remove();
    this.wake.remove();
    this.renderer.app.ticker.stop();
    this.renderer.app.destroy({ removeView: true });
    if (import.meta.env.DEV) {
      const w = window as unknown as { onirya?: Game };
      if (w.onirya === this) delete w.onirya;
    }
  }

  private afterWake(): void {
    if (this.phase === "won") {
      this.hooks.onWinContinue();
      return;
    }
    this.returnToTitle();
  }

  private bindInput(): void {
    this.input = new PointerInput(
      this.renderer.canvas,
      () => config.playerCurrent.accelRef,
      () => config.playerCurrent.accelMax,
      () => config.playerCurrent.touchDwellMs,
      () => config.playerCurrent.touchSlopPx,
    );

    this.input.onBubbleStart((p) => {
      if (!this.playing()) return;
      if (!this.divider.allowsSpawn(p.x, p.y)) return;
      this.audio.unlock();
      this.bubbles.startGrow(p.x, p.y, p.id, this.divider);
    });
    this.input.onBubbleMove((p) => {
      this.bubbles.moveGrow(p.id, p.x, p.y, this.divider);
    });
    this.input.onBubbleEnd((p) => {
      this.bubbles.release(p.id);
    });
    this.input.onCurrentStroke((p) => {
      if (this.phase !== "play" && this.phase !== "title") return;
      this.field.injectStroke(p.x, p.y, p.dx, p.dy, p.intensity);
      if (!this.playing()) return;
      this.audio.unlock();
      this.audio.noticeCurrent(p.intensity / Math.max(0.5, config.playerCurrent.accelMax));
    });
    this.input.onHover((p) => {
      if (!this.playing()) return;
      this.noticeHover(p.x, p.y, p.intensity);
      this.renderer.trail.push(p.x, p.y);
    });
    this.input.onHoverEnd(() => {
      this.hasHover = false;
      this.hoverBubbleId = null;
      this.audio.gongRelease();
      this.renderer.trail.lift();
    });
  }

  private noticeHover(x: number, y: number, speed: number): void {
    this.hoverX = x;
    this.hoverY = y;
    this.hasHover = true;
    const hit = this.bubbleAt(x, y);
    if (hit && hit.state !== "Growing") {
      this.hoverOnLine =
        this.divider.distanceTo(x, y) <= Math.max(14, config.divider.thickness * 0.55 + 6);
      this.hoverAbove = this.divider.isAbove(x, y);
      this.lastPluckX = Number.NaN;
      return;
    }
    this.noticeLinePluck(x, y, speed);
  }

  private bubbleAt(x: number, y: number) {
    let hit: (typeof this.bubbles.bubbles)[number] | null = null;
    let best = Infinity;
    for (const bubble of this.bubbles.bubbles) {
      if (bubble.state === "Absorbed") continue;
      const dx = bubble.x - x;
      const dy = bubble.y - y;
      const d2 = dx * dx + dy * dy;
      if (d2 > bubble.radius * bubble.radius) continue;
      if (d2 < best) {
        best = d2;
        hit = bubble;
      }
    }
    return hit;
  }

  private bubbleSizeNorm(radius: number): number {
    const minR = Math.max(1, config.bubble.minRadius);
    const createdMax = Math.max(minR + 1, config.bubble.maxRadius);
    const mergedMax = Math.max(createdMax + 1, createdMax * config.merge.absorbScale);
    const r = Math.max(radius, minR);
    if (r <= createdMax) {
      const t = Math.log(r / minR) / Math.log(createdMax / minR);
      return clamp(t * 0.72, 0, 0.72);
    }
    const t = Math.log(r / createdMax) / Math.log(mergedMax / createdMax);
    return clamp(0.72 + t * 0.28, 0.72, 1);
  }

  private syncHoverGong(): void {
    if (!this.hasHover) {
      if (this.hoverBubbleId !== null) {
        this.hoverBubbleId = null;
        this.audio.gongRelease();
      }
      return;
    }
    const hit = this.bubbleAt(this.hoverX, this.hoverY);
    if (!hit || hit.state === "Growing") {
      if (this.hoverBubbleId !== null) {
        this.hoverBubbleId = null;
        this.audio.gongRelease();
      }
      return;
    }
    const size = this.bubbleSizeNorm(hit.radius);
    if (hit.id !== this.hoverBubbleId) {
      this.hoverBubbleId = hit.id;
      this.audio.gong(size, hit.bornAbove);
    } else {
      this.audio.gongFollow(size, hit.bornAbove);
    }
  }

  private noticeLinePluck(x: number, y: number, speed: number): void {
    const dist = this.divider.distanceTo(x, y);
    const band = Math.max(14, config.divider.thickness * 0.55 + 6);
    const on = dist <= band;
    const above = this.divider.isAbove(x, y);
    const xNorm = clamp(x / Math.max(1, this.bounds.width), 0, 1);
    const speedN = clamp(speed / 900, 0, 1);
    const crossed = this.hoverAbove !== null && above !== this.hoverAbove;
    const entered = on && !this.hoverOnLine;
    const travel =
      on && Number.isFinite(this.lastPluckX) && Math.abs(x - this.lastPluckX) > this.bounds.width * 0.045;
    if (entered || crossed || travel) {
      this.audio.cling(xNorm, speedN);
      this.lastPluckX = x;
    }
    if (!on) this.lastPluckX = Number.NaN;
    this.hoverOnLine = on;
    this.hoverAbove = above;
  }

  private tick(deltaMs: number): void {
    this.time.tick(deltaMs, config.physics.timeStepCapMs);
    const dt = this.time.delta;
    const t = this.time.elapsed;

    this.merge.beginFrame();
    this.field.update(dt);
    this.bubbles.update(dt, t, this.divider, this.field);
    this.divider.update(dt, t, this.field);
    this.physics.update(Math.min(deltaMs, config.physics.timeStepCapMs));
    this.renderer.particles.update(dt, t, this.field, this.divider);
    this.renderer.trail.update(dt, this.divider);
    this.renderer.draw(
      this.bubbles.bubbles,
      this.divider,
      this.field,
      t,
      this.spectrumProgress(),
      dt,
    );
    if (this.phase === "title") {
      this.intro.update(dt, t, this.field, this.divider, this.renderer.particles);
    } else if (this.playing()) {
      this.syncHoverGong();
      if (this.shouldLose()) this.beginLose();
      else if (this.shouldWin()) this.beginWin();
    } else {
      this.tickEnding(dt);
    }
    this.audio.update(dt, this.audioFrame());
    this.syncHud();
  }

  private syncHud(): void {
    if (!this.hud) return;
    const lose = this.level.rules.lose;
    const { first, second } = this.divider.regionShares();
    this.hud.setMeters({
      score: this.score,
      winAt: config.score.winAt,
      showScore: this.level.rules.win.type === "score",
      thickness: config.divider.thickness,
      thicknessMax: config.divider.thicknessMax,
      showLine: lose.type === "divider-max" || lose.type === "divider-or-region",
      regionFirst: first,
      regionSecond: second,
      regionLimit:
        (lose.type === "region-share" || lose.type === "divider-or-region") &&
        config.divider.spaceLoseAt > 0
          ? config.divider.spaceLoseAt
          : null,
      oval: this.divider.isOval,
    });
  }

  private spectrumProgress(): number {
    const t = clamp(this.score / Math.max(1, config.score.winAt), 0, 1);
    return bezierEaseInOut(t, config.score.auroraBezierIn, config.score.auroraBezierOut);
  }

  private shouldLose(): boolean {
    const lose = this.level.rules.lose;
    if (lose.type === "none") return false;
    if (lose.type === "divider-max" || lose.type === "divider-or-region") {
      if (config.divider.thickness >= config.divider.thicknessMax - 0.05) return true;
    }
    if (lose.type === "region-share" || lose.type === "divider-or-region") {
      const limit = config.divider.spaceLoseAt;
      if (limit > 0) {
        const { first, second } = this.divider.regionShares();
        if (first >= limit || second >= limit) return true;
      }
    }
    return false;
  }

  private shouldWin(): boolean {
    if (this.level.rules.win.type === "score") {
      return this.score >= config.score.winAt;
    }
    return false;
  }

  private spawnLevelObjects(): void {
    for (const obj of this.level.objects) this.spawnObject(obj);
  }

  private spawnObject(obj: LevelObject): void {
    switch (obj.kind) {
      default:
        return;
    }
  }

  private beginPlay(): void {
    if (this.phase !== "title") return;
    this.audio.unlock();
    this.intro.dismiss();
    this.phase = "play";
  }

  private snapshotLine(): LineLive {
    const d = config.divider;
    return {
      thickness: d.thickness,
      wiggleAmp: d.wiggleAmp,
      sizePulseAmp: d.sizePulseAmp,
      wiggleSpeed: d.wiggleSpeed,
      sizePulseSpeed: d.sizePulseSpeed,
    };
  }

  private applyLine(live: LineLive): void {
    const d = config.divider;
    d.thickness = live.thickness;
    d.wiggleAmp = live.wiggleAmp;
    d.sizePulseAmp = live.sizePulseAmp;
    d.wiggleSpeed = live.wiggleSpeed;
    d.sizePulseSpeed = live.sizePulseSpeed;
  }

  private beginWin(): void {
    if (this.phase !== "play") return;
    this.phase = "win";
    this.winT = 0;
    this.hasHover = false;
    this.hoverBubbleId = null;
    this.audio.gongRelease();
    this.wake.begin("win");
    this.wake.setWhite(0);
  }

  private beginLose(): void {
    if (this.phase !== "play") return;
    this.phase = "lose";
    this.winT = 0;
    this.hasHover = false;
    this.hoverBubbleId = null;
    this.audio.gongRelease();
    this.wake.begin("lose");
    this.wake.setWhite(0);
  }

  private tickEnding(dt: number): void {
    const fading = this.phase === "win" || this.phase === "lose";
    const isWin = this.phase === "win" || this.phase === "won";
    if (fading) {
      this.winT += dt;
      const u = clamp(this.winT / 3.4, 0, 1);
      const eased = bezierEaseInOut(u, 0.42, 0.58);
      const rem = isWin ? clamp((u - 0.08) / 0.82, 0, 1) : 0;
      this.wake.setWhite(eased);
      this.audio.setWin(eased, rem);
      if (u >= 1) {
        this.phase = isWin ? "won" : "lost";
        this.winT = 0;
        this.wake.showPhrase();
      }
      return;
    }
    this.winT += dt;
    this.audio.setWin(1, isWin ? 1 : 0);
    if (this.winT >= 1.15) this.wake.arm();
  }

  private returnToTitle(): void {
    this.resetWorld();
    this.intro.reveal();
    this.wake.reset();
    this.phase = "title";
  }

  private resetWorld(): void {
    this.bubbles.clear();
    this.divider.reset();
    this.field.stored.fill(0);
    this.applyLine(this.lineBaseline);
    this.score = 0;
    this.syncHud();
    this.winT = 0;
    this.hasHover = false;
    this.hoverBubbleId = null;
    this.hoverOnLine = false;
    this.hoverAbove = null;
    this.lastPluckX = Number.NaN;
    this.audio.gongRelease();
    this.audio.resetWin();
    this.renderer.trail.clear();
  }

  private audioFrame() {
    const cfg = config.divider;
    const inflate = clamp((cfg.thickness - 8) / Math.max(1, cfg.thicknessMax - 8), 0, 1);
    const wiggle = clamp(cfg.wiggleAmp / Math.max(1, cfg.wiggleAmpMax), 0, 1);
    const pulse = clamp(cfg.sizePulseAmp / Math.max(0.05, cfg.sizePulseAmpMax), 0, 1);
    const wiggleRate = clamp(cfg.wiggleSpeed / Math.max(0.1, cfg.wiggleSpeedMax), 0, 1);
    const pulseRate = clamp(cfg.sizePulseSpeed / Math.max(0.1, cfg.sizePulseSpeedMax), 0, 1);

    let upperMass = 0;
    let lowerMass = 0;
    let upperCount = 0;
    let lowerCount = 0;
    let upperSize = 0;
    let lowerSize = 0;
    let growing = 0;
    let meeting = 0;
    let speedSum = 0;
    let speedCount = 0;
    const maxR = Math.max(1, config.bubble.maxRadius);

    for (const bubble of this.bubbles.bubbles) {
      const n = clamp(bubble.radius / maxR, 0, 2);
      if (bubble.bornAbove) {
        upperMass += n;
        upperCount += 1;
        upperSize += n;
      } else {
        lowerMass += n;
        lowerCount += 1;
        lowerSize += n;
      }
      if (bubble.state === "Growing") growing += n;
      if (bubble.state === "Meeting") meeting = Math.max(meeting, bubble.meetGlow);
      if (bubble.state !== "Growing") {
        const v = bubble.body.velocity;
        speedSum += Math.hypot(v.x, v.y);
        speedCount += 1;
      }
    }

    const stored = this.field.stored;
    let gustEnergy = 0;
    for (let i = 0; i < stored.length; i += 2) {
      gustEnergy += stored[i] * stored[i] + stored[i + 1] * stored[i + 1];
    }
    const gustRms = Math.sqrt(gustEnergy / Math.max(1, stored.length / 2));

    return {
      inflate,
      wiggle,
      pulse,
      wiggleRate,
      pulseRate,
      lineHeat: clamp(this.divider.bonusSpheres / 48, 0, 1),
      upperMass: clamp(upperMass / 4, 0, 1),
      lowerMass: clamp(lowerMass / 4, 0, 1),
      upperCount,
      lowerCount,
      avgUpper: upperCount > 0 ? upperSize / upperCount : 0,
      avgLower: lowerCount > 0 ? lowerSize / lowerCount : 0,
      growing: clamp(growing / 3, 0, 1),
      meeting,
      tension: clamp(this.bubbles.bubbles.length / Math.max(1, config.bubble.maxCount), 0, 1),
      flow: clamp((speedCount > 0 ? speedSum / speedCount : 0) / 28, 0, 1),
      gust: clamp(gustRms / Math.max(1e-6, config.field.maxStored), 0, 1),
    };
  }
}
